import React, { useEffect, useState } from 'react';
import { StackedBarChartWidget } from './index';
import { NonIdealState } from '@blueprintjs/core';
import { convertToStacks, Stacks } from '../chartUtils';
import { DefaultChartColorScales } from 'styles/chartColors';
import {NO_DATA_MESSAGE} from '../chartUtils'
import { useWidgetControlsContext } from 'views/common/WidgetControls';

/**
 * ChartWrapper for the Stacked Bar Graph.
 *
 * @param getColorScale
 * @param widgetConfiguration
 * @param dimensions
 * @param data
 * @param endpointQuery
 * @returns {*}
 * @constructor
 */
export function StackedBarGraphWrapper({ getColorScale, widgetConfiguration, dimensions, data, endpointQuery, noDataMessage=NO_DATA_MESSAGE}) {
	const [convertedData, setConvertedData] = useState<null | Stacks>(null);
	const {dispatch, state} = useWidgetControlsContext();
	const hideLabels = state?.hideLabels;
	useEffect(() => {
		// the data is null or in the wrong shape and we exit
		if (!data) {
			dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: false });
			return;
		}


		// empty data is "valid"
		if (!data.entries || data.entries?.length === 0) {
			dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: false });
			setConvertedData({ data: [], properties: [], keys: [], rolled: [], extent: [0, 0] });
			return;
		}
		dispatch && dispatch({ type: "setLabelsToggleBtnStatus", payload: true })

		// data comes in as {key, value, children:[{key1, value}, {key2, value}...]}
		// need as {label:value, key1: value, key2: value}
		const converted = convertToStacks(data);

		// update the color scale and update the data's color property
		// we need to do 2 things:
		// a) set the domain for the colorScale
		// b) set the color property on the individual items
		// there are a few choices here:
		// Sequential: uses the index to determine a color
		// colorByValue: uses the value obviously
		// colorByProperty: uses the value of a given property in metadata
		// else we default to using the key
		converted.colorPropertyMap = {};

		if (widgetConfiguration?.colorScale === DefaultChartColorScales.Sequential) {
			getColorScale.domain([0, converted?.keys?.length || 0]);
			converted.keys.forEach((d, index) => {
				converted.colorPropertyMap[d] = index;
			});
		} else if (widgetConfiguration?.colorProperty) {
			getColorScale.domain(converted?.keys);
			converted.data.forEach((d) => {
				//widgetConfiguration.colorProperty is array of keys to get to the property that will be used to determine color.
				converted.colorPropertyMap[d.label] = widgetConfiguration.colorProperty.reduce((p, key) => p[key], d);
			});
		} else {
			getColorScale.domain(converted?.keys);
			converted.keys.forEach((d) => {
				converted.colorPropertyMap[d] = d;
			});
		}

		setConvertedData(converted);
	}, [data, getColorScale, widgetConfiguration]);


	//If the state is not ideal (the data is null or empty) render the nonIdealState message
	return convertedData?.data?.length > 0 ? (
		<StackedBarChartWidget
			data={convertedData}
			dimensions={dimensions}
			widgetConfiguration={widgetConfiguration}
			endpointQuery={endpointQuery}
			getColor={getColorScale}
			hideLabels={hideLabels}
		/>
	) : (
		<NonIdealState
			title="No Data Found"
			icon="graph-remove"
			description={noDataMessage}
		/>
	);
}
